import {observer} from 'mobx-react-lite';
import React, {useContext, useEffect} from 'react';
import {StoreContext} from '../base/root-store';
import {WebappBase} from './WebappBase';
import {
    IButtonProps,
    IChartDataProps,
    IContentFiltersProps,
    IFilterItemProps,
    IFilterProps,
    IPageOptionProps,
    IPageSelectProps,
    IPageTitleAndFiltersProps,
    IQuestionProps,
    ISelectSingleProps,
    ITemplateDashboardFeedbackProps,
    ITemplateDashboardQuestionsProps,
    ITemplateGeneralCardProps,
    ITemplateGeneralRowProps,
    ITemplatePlainTextCardProps,
    Main,
    ModalProvider,
    Notification,
    TemplateDashboard,
    TFilterValue,
} from '../../../patterns';
import {
    BusinessUnitDataForOnboardingDashboard,
    FeedbackAnswerWithComment,
    OnboardingAnswerWithComment,
    OnboardingStore,
    SingleQuestionCard,
} from './onboarding-store';
import {IAnswerProps} from '../../../patterns/components/feedback-answers/feedback-answers';
import {formatDateRange, formatToDayMonthAndYear, periodIdFromRange, rangeFromPeriodId} from '../base/dateUtils';
import {IFeedbackItemProps} from '../../../patterns/components/feedback/feedback';
import {OnboardingCompanyHelpModal} from './OnboardingCompanyHelpModal';
import {OnboardingFeedbackHelpModal} from './OnboardingFeedbackHelpModal';
import {ScoreTooltip} from '../base/ScoreTooltip';
import {DashboardData, DashboardPageTab} from './team-results-store';
import {UserStore} from '../user/user-store';
import {DateRange} from 'react-day-picker';
import {ISelectDatepickerProps} from '../../../patterns/components/forms/select/select';
import {Language} from '../base/label-store';
import {getCategoryLanguage, isCustomCategory} from '../categories/CategoriesUtil';
import {OnboardingCategoryHelpModal} from './OnboardingCategoryHelpModal';

export const OnboardingDashboard = observer((): JSX.Element => {
    const store = useContext(StoreContext).onboardingStore;
    const userStore = useContext(StoreContext).userStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    if (store.state === 'error' && store.errorReason === 'user-deleted') {
        return <Main showBackground={true}>
            <Notification
                text={store.label('common.user.deleted.error-message')}
                defaultIsOpen={true}
                type="info"
            />
        </Main>;
    }

    return (
        <WebappBase
            store={store}
            title={store.label('dashboard.title')}
            renderChildren={(): React.ReactNode => (
                <React.Fragment>
                    <ModalProvider
                        isOpen={store.variables.openModal !== undefined}
                        onToggle={(): void => store.closeModal()}>
                        {store.variables.openModal === 'onboarding-help-company' && <OnboardingCompanyHelpModal/>}
                        {store.variables.openModal === 'onboarding-help-feedback' && <OnboardingFeedbackHelpModal/>}
                        {store.variables.openModal === 'onboarding-help-category' && <OnboardingCategoryHelpModal/>}
                    </ModalProvider>
                    <TemplateDashboard
                        pageTitleAndFilters={pageTitleAndFilters(store, userStore)}
                        company={companyForOnboardingPageProps(store)}
                        feedback={feedbackForOnboardingDashboardProps(store, userStore)}
                        questions={onboardingQuestionProps(store, userStore)}
                        noData={noOnboardingDataAlert(store)}
                        categories={categoryProps(store, userStore)}
                    />
                </React.Fragment>
            )}
        />
    );
});

const pageTitleAndFilters = (store: OnboardingStore, userStore: UserStore): IPageTitleAndFiltersProps => ({
    title: store.label('dashboard.title'),
    dashboardSwitcher: pageSwitcherProps(store, userStore),
    contentFilters: onboardingPageFilterProps(store, userStore),
});

const pageSwitcherProps = (store: OnboardingStore, userStore: UserStore): IFilterProps | undefined => {
    const isAdminOrManager = userStore.payload?.hasManagerRights === true || userStore.payload?.isAdmin === true;
    const isPersonalDashBoardEnabled = userStore.payload?.isPersonalDashboardEnabled === true;
    const allPageSwitcherItems: IFilterItemProps[] = [
        {
            id: 'team-results',
            name: 'filter',
            label: store.label('dashboard.switcher.team-results'),
            value: 'team-results',
        },
        {
            id: 'new-hires',
            name: 'filter',
            label: store.label('dashboard.switcher.new-hires'),
            value: 'new-hires',
            count: userStore.payload?.peopleInOnboardingCount !== undefined && userStore.payload?.hasManagerRights === true ? userStore.payload?.peopleInOnboardingCount : undefined,
        },
        {
            id: 'my-results',
            name: 'filter',
            label: store.label('dashboard.switcher.my-results'),
            value: 'my-results',
            isRedBackground: true,
        },
    ];

    let eligibleSwitcherItems: IFilterItemProps[];

    if (isPersonalDashBoardEnabled && isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems;
    } else if (isPersonalDashBoardEnabled && !isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems.filter((item) => item.id !== 'new-hires');
    } else if (!isPersonalDashBoardEnabled && isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems.filter((item) => item.id !== 'my-results');
    } else {
        eligibleSwitcherItems = [];
    }

    return eligibleSwitcherItems.length !== 0 ? ({
        label: 'Filter label',
        onChange: (value: TFilterValue): void => {
            store.onDashboardTabChange(value as DashboardPageTab);
        },
        value: store.variables.openTab,

        items: eligibleSwitcherItems,
    }) : undefined;
};

const onboardingPageFilterProps = (store: OnboardingStore, userStore: UserStore): IContentFiltersProps => ({
    filterValues: [{
        oneFilterValue: categoriesFilterProps(store, userStore),
    },
    {
        oneFilterValue: businessUnitFilterProps(store),
    },
    {
        oneFilterValue: compareToFilterProps(store),
    },
    {
        oneFilterValue: store.payload!.onboardingFilter.locations.length > 2 ? locationFilterProps(store) : undefined,
    },
    {
        oneFilterValue: timeframeFilterProps(store),
    },
    ],
});

const businessUnitFilterProps = (store: OnboardingStore): IPageSelectProps => {
    const filterOptions = store.payload!.onboardingFilter.businessUnits;

    const options = filterOptions.map((filterOption) => ({
        label: filterOption.label,
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('common.filter.department.title'),
        name: 'Department',
        id: 'onboarding-department',
        defaultValue: filterOptions.find((option) => option.selected)?.id ?? filterOptions[0]?.id,
        search: {
            id: 'department-search',
            name: 'Search',
            label: store.label('common.filter.search.label'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: (filterOptionsId): void => store.onBusinessUnitChange('' + filterOptionsId),
        type: undefined,
    });
};

const categoryProps = (store: OnboardingStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.categoryCardForOnboardingDashboard?.categories === undefined) {
        return undefined;
    } else {
        const categoryRows = store.payload!.categoryCardForOnboardingDashboard.categories
            .filter((categoryData) => categoryData.barpoint.value.answered !== 0)
            .map((row) => dataRow(store, row, 'category', userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openCategoryHelpModal(),
            },
        ];

        return ({
            title: store.label('dashboard.categories.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.categories.header.category'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: categoryRows,
            },
            more: {showExpand: true},
        });
    }
};

const dataRow = (store: OnboardingStore, data: DashboardData, translationKey: string, userStore: UserStore): ITemplateGeneralRowProps => {
    const score = data.barpoint.value.scorePercentage;
    const compareScore = data.barpoint.compareTo.scorePercentage;
    const allValueLabelKey = data.value === 'ALL' ? store.label(`common.${translationKey}.all`) : data.value;
    const labelKey = translationKey === 'category' ? getCategoryName(store, userStore.payload!.language, data.value) : `common.${translationKey}.${data.value}`;
    const tooltipLabelKey = isCustomCategory(data.value) ? labelKey : `common.${translationKey}.description.` + 'ema';

    let scoreChangeColor: string;

    if (data.scoreChange > 0) {
        scoreChangeColor = 'green';
    } else if (data.scoreChange < 0) {
        scoreChangeColor = 'red';
    } else {
        scoreChangeColor = 'grey';
    }

    return ({
        id: data.value,
        title: translationKey === 'roles' || translationKey === 'tags' ? allValueLabelKey : store.label(labelKey),
        titleTooltip: translationKey === 'category' ? store.label(tooltipLabelKey) : undefined,
        score: {
            progress: {
                percent: score,
                color: score >= compareScore ? 'green' : 'red',
                indicator: compareScore,
            },
            indicator: {
                number: score,
                color: score >= compareScore ? 'green' : 'red',
            },
        },
        scoreTooltip: <ScoreTooltip
            period={store.payload!.onboardingTooltipPayload.period}
            name={store.payload!.onboardingTooltipPayload.businessUnitName}
            compareToName={store.payload!.onboardingTooltipPayload.compareToName}
            value={data.barpoint.value}
            compareTo={data.barpoint.compareTo}
        />,
        change: {
            number: data.scoreChange,
            color: scoreChangeColor,
            isNumberNotVisible: true,
        },
        activity: {
            percent: data.barpoint.value.participationPercentage,
            color: 'blue',
            indicator: data.barpoint.compareTo.participationPercentage,
        },
        activityTooltip: <ScoreTooltip
            period={store.payload!.onboardingTooltipPayload.period}
            name={store.payload!.onboardingTooltipPayload.businessUnitName}
            compareToName={store.payload!.onboardingTooltipPayload.compareToName}
            value={data.barpoint.value}
            compareTo={data.barpoint.compareTo}
        />,
        isFocused: data.focused,
    } as ITemplateGeneralRowProps);
};

const locationFilterProps = (store: OnboardingStore): IPageSelectProps => {
    const filterOptions = store.payload!.onboardingFilter.locations;

    const options = filterOptions.map((filterOption) => ({
        label: store.label(`common.location.${filterOption.id}`),
        value: filterOption.id,
    }));

    return ({
        label: store.label('common.filter.location.title'),
        id: 'onboarding-location',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        options,
        onChange: ((filterOptionId) => store.onLocationChange('' + filterOptionId)),
    } as IPageSelectProps);
};

const categoriesFilterProps = (store: OnboardingStore, userStore: UserStore): IPageSelectProps | undefined => {
    const filterOptions = store.payload?.onboardingFilter.categories;

    if (!filterOptions || filterOptions.length === 0) {
        return undefined;
    }

    const options = filterOptions.map((filterOption) => ({
        label: getCategoryName(store, userStore.payload!.language, filterOption.id),
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('dashboard.filter.categories.title'),
        id: 'onboarding-category',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        search: {
            id: 'categories-search',
            name: 'categories',
            label: store.label('common.filter.search.label.categories'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: ((filterOptionsId) => store.onCategoryChange('' + filterOptionsId)),
    } as IPageSelectProps);
};

const compareToFilterProps = (store: OnboardingStore): IPageSelectProps => {
    const filterOptions = store.payload!.onboardingFilter.compareTo;

    const options = filterOptions.map((filterOption) => ({
        label: filterOption.label,
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('common.filter.compareto.title'),
        id: 'onboarding-compare',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        search: {
            id: 'Compare-to-search',
            name: 'Compare-to',
            label: store.label('common.filter.search.label'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: (filterOptionsId): void => store.onCompareToChange('' + filterOptionsId),
    } as IPageSelectProps);
};

const timeframeFilterProps = (store: OnboardingStore): ISelectSingleProps => {
    const filterOptions = store.payload!.onboardingFilter.periods;

    const options = filterOptions.map((filterOption) => ({
        label: store.timeframeFilterLabels(filterOption.id.split('_')[1]),
        value: filterOption.id,
    }));

    return ({
        label: store.label('common.filter.period.title'),
        id: 'onboarding-time',
        defaultValue: store.variables.isCustomDate ? undefined : filterOptions.find((filterOption) => filterOption.selected)?.id,
        options,
        datepicker: getDatepickerProps(store),
        onChange: ((filterOptionId: string) => {
            store.setCustomDateSelected(false);
            store.onPeriodChange(filterOptionId);
        }),
    } as ISelectSingleProps);
};

const getDatepickerProps: (store: OnboardingStore) => ISelectDatepickerProps = (store: OnboardingStore) => ({
    datepickerProps: {
        format: 'dd.MM.yyyy',
        type: 'range',
        defaultValue: {from: undefined, to: undefined} as DateRange,
        field: [
            {
                label: store.label('user-menu.datepicker.from'),
                id: 'datepicker-from',
                name: 'datepicker-from',
            },
            {
                label: store.label('user-menu.datepicker.to'),
                id: 'datepicker-to',
                name: 'datepicker-to',
            },
        ],
    },
    onChange: (data): void => {
        const range = (data as DateRange);
        const {from, to} = range;

        if (to && from) {
            store.setCustomDateSelected(true);
            store.onPeriodChange(periodIdFromRange(range));
        }
    },
    customDateDisplayValue: store.variables.isCustomDate && store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : undefined,
});

const companyForOnboardingPageProps = (store: OnboardingStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.companyCardForOnboardingDashboard === undefined) {
        return undefined;
    } else {
        const defaultOpenItems: string[] = [];

        let openBusinessUnit = store.payload!.companyCardForOnboardingDashboard.rootBusinessUnitData;

        while (!openBusinessUnit.focused) {
            defaultOpenItems.push(openBusinessUnit.id);
            openBusinessUnit = openBusinessUnit.children[0];
        }

        defaultOpenItems.push(openBusinessUnit.id);

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openCompanyHelpModal(),
            },
        ];

        return ({
            title: store.label('onboarding.company.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('onboarding.company.header.department'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('onboarding.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('onboarding.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: store.payload!.companyCardForOnboardingDashboard.rootBusinessUnitData.barpoint.value.answered < 1 ? {
                    type: 'info',
                    text: store.label('onboarding.not-enough-data'),
                } : [
                    businessUnitRow(store, store.payload!.companyCardForOnboardingDashboard.rootBusinessUnitData, 'exclude-children', store.label('onboarding.company.all-departments')),
                    ...store.payload!.companyCardForOnboardingDashboard.rootBusinessUnitData.children.map((child) => businessUnitRow(store, child, 'include-children')),
                ],
                defaultOpenItems,
            },
            more: {showExpand: true},
        });
    }
};

const businessUnitRow = (store: OnboardingStore, businessUnitData: BusinessUnitDataForOnboardingDashboard, mode: 'include-children' | 'exclude-children', titleOverride?: string): ITemplateGeneralRowProps => {
    const score = businessUnitData.barpoint.value.scorePercentage;
    const compareScore = businessUnitData.barpoint.compareTo.scorePercentage;

    return ({
        id: businessUnitData.id,
        title: titleOverride === undefined ? businessUnitData.title : titleOverride,
        score: {
            progress: {
                percent: score,
                color: score >= compareScore ? 'green' : 'red',
                indicator: compareScore,
            },
            indicator: {
                number: score,
                color: score >= compareScore ? 'green' : 'red',
            },
        },
        scoreTooltip: <ScoreTooltip
            period={store.payload!.onboardingTooltipPayload.period}
            name={businessUnitData.title}
            compareToName={store.payload!.onboardingTooltipPayload.compareToName}
            value={businessUnitData.barpoint.value}
            compareTo={businessUnitData.barpoint.compareTo}
        />,
        change: {
            number: businessUnitData.scoreChange,
            color: businessUnitData.scoreChange >= 0 ? 'green' : 'red',
            isNumberNotVisible: true,
        },
        activity: {
            percent: businessUnitData.barpoint.value.participationPercentage,
            color: 'blue',
            indicator: businessUnitData.barpoint.compareTo.participationPercentage,
        },
        activityTooltip: <ScoreTooltip
            period={store.payload!.onboardingTooltipPayload.period}
            name={businessUnitData.title}
            compareToName={store.payload!.onboardingTooltipPayload.compareToName}
            value={businessUnitData.barpoint.value}
            compareTo={businessUnitData.barpoint.compareTo}
        />,
        isFocused: businessUnitData.focused,
        subItems: mode === 'include-children' ? businessUnitData.children.map((child) => businessUnitRow(store, child, 'include-children')) : [],
    } as ITemplateGeneralRowProps);
};

const feedbackForOnboardingDashboardProps = (store: OnboardingStore, userStore: UserStore): ITemplateDashboardFeedbackProps | undefined => {
    if (store.payload!.feedbackCardForOnboardingDashboard === undefined) {
        return undefined;
    } else {
        const questions: IFeedbackItemProps[] = store.payload!.feedbackCardForOnboardingDashboard.questionFeedback.map((question) => {
            const answers = question.answers.map((answer: FeedbackAnswerWithComment): IAnswerProps => ({
                time: {
                    value: formatToDayMonthAndYear(answer.datetime),
                },
                reactionType: answer.answer === 'null' ? 'talk-bubble' : 'thumb',
                direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
                text: store.translateComment(answer),
                commentToolTip: (userStore.payload?.isAdmin || userStore.payload?.hasManagerRights) ? answer.questionBusinessUnitName : undefined,
            }));

            return {
                title: getCategoryName(store, userStore.payload!.language, question.categoryId),
                question: question.questionText,
                answers: {answers},
                count: answers.length,
                id: question.onboardingQuestionId,
            } as IFeedbackItemProps;
        });

        const buttonProps: IButtonProps[] = [
            {
                color: 'white',
                icon: 'translate',
                onClick: () : Promise<void> => store.onTranslateCommentsButtonClicked(),
                tooltip: store.translateCommentsEnabled ? store.label('dashboard.feedback.show-original-comments') : store.label('dashboard.feedback.translate-comments'),
                isLoading: store.isTranslationButtonLoading,
            },
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openFeedbackHelpModal(),
            },
        ];

        return ({
            title: store.label('onboarding.feedback.title'),
            actions: buttonProps,
            data: {
                items: questions.length === 0 ? {
                    type: 'info',
                    text: store.label('onboarding.not-enough-data'),
                } : questions,
            },
            more: {showExpand: true},
        });
    }
};

const onboardingQuestionProps = (store: OnboardingStore, userStore: UserStore): ITemplateDashboardQuestionsProps | undefined => {
    if (store.payload!.questionCardsForOnboardingDashboard === undefined) {
        return undefined;
    } else {

        return ({
            items: store.payload!.questionCardsForOnboardingDashboard.questions.map((question) => onboardingQuestionCardProps(store, question, userStore.payload!.language)),
        });
    }
};

const onboardingQuestionCardProps = (store: OnboardingStore, question: SingleQuestionCard, language: Language): IQuestionProps => {
    const score = question.barpoint.value.scorePercentage;
    const activity = question.barpoint.value.participationPercentage;

    const chartScores = question.chartScores.length === 1 ? [question.chartScores[0], question.chartScores[0]] : question.chartScores;

    const answers = question.feedbackAnswers.map((answer: OnboardingAnswerWithComment): IAnswerProps => ({
        reactionType: answer.answer === 'null' ? 'talk-bubble' : 'thumb',
        direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
        text: answer.text,
        time: {
            value: formatToDayMonthAndYear(answer.datetime),
        },
    }));

    return ({
        // eslint-disable-next-line eqeqeq
        category: question.categoryId != null ? getCategoryName(store, language, question.categoryId) : store.label(`common.category.ONBOARDING`),
        text: question.questionText,
        answers: {answers},
        bars: [
            {
                icon: 'users',
                color: 'blue',
                label: store.label('onboarding.questions.activity', {
                    number_of_answers: question.barpoint.value.answered.toString(),
                    number_of_questions_asked: question.barpoint.value.prepared.toString(),
                }),
                percent: activity,
            },
            {
                icon: 'like',
                color: score > 50 ? 'green' : 'red',
                label: store.label('onboarding.questions.score', {score_percentage: score.toString()}),
                percent: score,
            },
        ],
        chart: {
            id: 'chart-1',
            data: chartScores.map((chartScore: number, index: number): IChartDataProps => ({
                name: index + 1,
                a: chartScore,
            })),
            areas: [
                {
                    key: 'a',
                    color: score > 50 ? 'greenPastel' : 'redPastel',
                    strokeWidth: 1,
                    lineColor: score > 50 ? 'green' : 'red',
                },
            ],
            showTooltip: false,
            aspect: 6.57,
            hideDots: true,
            hideGrid: true,
            hideXAxis: true,
            hideYAxis: true,
        },
    });
};

const noOnboardingDataAlert = (store: OnboardingStore): ITemplatePlainTextCardProps | undefined => {
    if (store.payload!.companyCardForOnboardingDashboard !== undefined && store.payload!.feedbackCardForOnboardingDashboard !== undefined) {
        return undefined;
    } else {
        return ({
            text: {
                rows: {
                    text: store.label('onboarding.no-employees-in-onboarding-alert'),
                    type: 'info',
                },
            },
        });
    }
};

const getCategoryName = (store: OnboardingStore, userLanguage: Language | undefined, catId: string): string => {
    const category = catId === 'ALL' ? store.categories?.find((c) => c.categoryName === 'ALL') : store.categories?.find((cat) => cat.id === catId);

    return category ? getCategoryLanguage(userLanguage!, category) : '';
};

